import React from 'react';

import { isAxiosError, isCancel } from 'axios';

import { APIRequest, SearchRequest } from '../api/types';

import Logger from '../logger';

export const useInfiniteSearch = <T>(
  request: (params?: SearchRequest) => APIRequest<T[]>,
  load = true,
  {
    pageSize = 20,
    debounceMs = 300,
    refreshKey,
  }: Partial<{ pageSize: number; debounceMs: number; refreshKey: number }> = {}
): {
  results: T[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  failed: boolean;
  failedStatus: number | undefined;
  appliedQuery: string;
  searchQuery: (phrase: string) => void;
  loadMore: () => void;
  retry: () => void;
} => {
  const [results, setResults] = React.useState<T[]>([]);
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(load);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [failedStatus, setFailedStatus] = React.useState<number | undefined>(undefined);
  const generation = React.useRef(0);
  const loaded = React.useRef(0);
  const inFlight = React.useRef(false);
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const fetchPage = async (offset: number): Promise<void> => {
    const current = generation.current;
    const setPageLoading = offset === 0 ? setLoading : setLoadingMore;
    inFlight.current = true;
    setPageLoading(true);
    setFailed(false);
    setFailedStatus(undefined);
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const pageRequest = request({ limit: pageSize, offset, query: query.trim() });
      if (pageRequest.config) {
        pageRequest.config.signal = controller.signal;
      }
      const { data } = await pageRequest();
      if (current !== generation.current) {
        return;
      }
      loaded.current = offset + data.length;
      setResults((previous) => (offset === 0 ? data : [...previous, ...data]));
      setHasMore(data.length === pageSize);
    } catch (error) {
      if (isCancel(error)) {
        return;
      }
      if (current === generation.current) {
        setFailed(true);
        setFailedStatus(isAxiosError(error) ? error.response?.status : undefined);
      }
      Logger.warn('Invalid response from the search API', error);
    } finally {
      if (current === generation.current) {
        inFlight.current = false;
        setPageLoading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!load && !query.trim()) {
      return;
    }
    generation.current += 1;
    loaded.current = 0;
    setLoadingMore(false);
    fetchPage(0);
  }, [request, load, query, pageSize, refreshKey]);

  React.useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      abortControllerRef.current?.abort();
    },
    []
  );

  const searchQuery = (phrase: string): void => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => setQuery(phrase), debounceMs);
  };

  const loadMore = (): void => {
    if (!hasMore || failed || inFlight.current) {
      return;
    }
    fetchPage(loaded.current);
  };

  const retry = (): void => {
    generation.current += 1;
    loaded.current = 0;
    setLoadingMore(false);
    fetchPage(0);
  };

  return {
    results,
    loading,
    loadingMore,
    hasMore,
    failed,
    failedStatus,
    appliedQuery: query,
    searchQuery,
    loadMore,
    retry,
  };
};
